import type { IApplicationState } from '@bridebook/web/lib/types';
import { z } from 'zod/v4';
import type {
  ClientActionAccess,
  ClientActionAccessRule,
  ClientActionAuthor,
  ClientActionDefinition,
} from './definition';
import { HookActionTypes } from './hooks/action-types';
import { CLIENT_ACTIONS, MODEL_CLIENT_ACTIONS } from './lexicon';

export const MAX_CLIENT_ACTIONS_PER_CALL = 5;

// The static `ClientAction` union and the runtime zod union both derive from the lexicon
// definitions, so the two cannot drift.

type ClientActionDefinitions = typeof CLIENT_ACTIONS;

type DefinitionToAction<D> = D extends { type: infer T; schema: infer S extends z.ZodType }
  ? { type: T; payload: z.infer<S> }
  : never;

export type ClientAction = DefinitionToAction<ClientActionDefinitions[number]>;

export type ClientActionType = ClientAction['type'];

// A batch as the executor takes it, from either author. It is wider than the model's input:
// the receipt's batch also carries the server-authored fact writes.
export type ClientActionBatch = { actions: readonly ClientAction[] };

// Type unions per action kind. The raw executor covers every `raw` type by itself; the hook
// executor must compose every `hook` type.
export type RawClientActionType = Extract<ClientActionDefinitions[number], { kind: 'raw' }>['type'];
export type HookClientActionType = Extract<
  ClientActionDefinitions[number],
  { kind: 'hook' }
>['type'];
export type ClientActionPayload<T extends ClientActionType> = Extract<
  ClientAction,
  { type: T }
>['payload'];

const CLIENT_ACTION_DEFINITIONS: Readonly<Record<string, ClientActionDefinition>> =
  Object.fromEntries(CLIENT_ACTIONS.map((definition) => [definition.type, definition]));

// ACL lookup. Unknown actions (e.g. version skew) count as disabled. A rule that depends on
// the payload gets the action's payload to decide.
export const getClientActionAccess = (type: string, payload: unknown): ClientActionAccess => {
  const access = getClientActionAccessRule(type);
  return typeof access === 'function' ? access(payload as never) : access;
};

// Whether the app already matches the payload, per the entry's `inForce` rule. An entry
// without one, or an unknown type, is never in force.
export const isClientActionInForce = (
  type: string,
  payload: unknown,
  state: IApplicationState,
): boolean => {
  const rule = CLIENT_ACTION_DEFINITIONS[type]?.inForce;
  return rule ? rule(payload as never, state) : false;
};

// The entry's ACL as written — a value, or a rule of the payload. Unknown types are disabled.
export const getClientActionAccessRule = (type: string): ClientActionAccessRule =>
  CLIENT_ACTION_DEFINITIONS[type]?.access ?? 'disabled';

// Whether the couple may set this action to run without asking.
export const canAlwaysAllow = (type: string): boolean =>
  CLIENT_ACTION_DEFINITIONS[type]?.alwaysAllow !== false;

// Payload schema lookup — the client re-validates before it dispatches.
export const getClientActionSchema = (type: string): z.ZodType | undefined =>
  CLIENT_ACTION_DEFINITIONS[type]?.schema;

// Who authors an action. An unknown type reads as the model's, and is refused as disabled.
export const getClientActionAuthor = (type: string): ClientActionAuthor =>
  CLIENT_ACTION_DEFINITIONS[type]?.authoredBy ?? 'model';

/**
 * The place a batch still waits on, in the couple's own words. A location is a resolved place,
 * so these words only prefill the field the couple picks it from. The card holds the batch
 * until they pick, and the server uses it to avoid a second field for the same place.
 */
export const locationPickQuery = (actions: readonly ClientAction[]): string | null => {
  for (const action of actions) {
    if (action.type === HookActionTypes.SET_WEDDING_LOCATION) {
      return action.payload.query;
    }
  }
  return null;
};

/**
 * Whether the couple can approve the batch yet. A batch can need consent and carry a place at
 * once. The executor reads the place pick, so the pick must be done (picked or skipped) first,
 * or the place reports as unsaved.
 */
export const canApproveBatch = (locationQuery: string | null, locationSettled: boolean): boolean =>
  locationQuery === null || locationSettled;

// The `{ type, payload }` envelope of one lexicon entry, typed from it.
type ActionEnvelope<D> = D extends {
  type: infer T extends string;
  schema: infer S extends z.ZodType;
}
  ? z.ZodObject<{ type: z.ZodLiteral<T>; payload: S }>
  : never;

// Built from the model's definitions only, so the model cannot name a disabled or
// server-authored action. The static union still covers the whole lexicon, since the client
// must refuse or run those too. The cast only asserts the non-empty tuple zod needs.
const actionEnvelopes = MODEL_CLIENT_ACTIONS.map((definition) =>
  z.strictObject({ type: z.literal(definition.type), payload: definition.schema }),
) as [
  ActionEnvelope<ClientActionDefinitions[number]>,
  ...ActionEnvelope<ClientActionDefinitions[number]>[],
];

export const clientActionSchema = z.discriminatedUnion('type', actionEnvelopes);

export const executeClientActionsInputSchema = z.strictObject({
  actions: z
    .array(clientActionSchema)
    .min(1)
    .max(MAX_CLIENT_ACTIONS_PER_CALL)
    .describe(
      'Actions to run in the app, in order. Each one is `{ "type": ..., "payload": { ... } }` — every argument sits inside `payload`, never beside `type`.',
    ),
});

export const executeClientActionsOutputSchema = z.strictObject({
  results: z.array(
    z.strictObject({
      type: z.string(),
      ok: z.boolean(),
      error: z.string().optional(),
      // What the action reported back: a hook action's own result, or the settled outcome
      // of a raw action whose entry declares one.
      data: z.unknown().optional(),
    }),
  ),
});

export type ExecuteClientActionsInput = z.infer<typeof executeClientActionsInputSchema>;
export type ExecuteClientActionsOutput = z.infer<typeof executeClientActionsOutputSchema>;
